// React
import { memo, useCallback, useMemo, useState } from "react";
import ReactDOM from "react-dom";

// Helpers
import { formatCurrency, useTranslate } from "helpers";

// Servisni sloj
import { initialInvoiceIncomingItemEditForm } from "services/api/finance/incoming-invoices/consts";
import {
  transformInvoiceIncomingItemFromForm,
  transformInvoiceIncomingItemToForm,
} from "services/api/finance/incoming-invoices/transformations";
import { InvoiceOutgoingItemEditFormValues } from "services/api/fleet/vehicles/invoices/types";
import { calculateBillingLineNet, calculateBillingLineTotal } from "services/api/finance/billing/calculations";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

// UI
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { TableSearch } from "ui/components/Table/TableSearch";
import { InvoiceItemForm } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleInvoiceSaleEditCard/components/InvoiceItemForm";

import { INVOICE_INCOMING_ITEMS_COLUMNS } from "./consts";
import { INVOICE_INCOMING_ITEM_EDIT_FORM_SCHEMA } from "./validation";
import { InvoiceIncomingEditItemsProps } from "./types";

/**
 * Stavke ručno unesenog ulaznog računa. Koristi isti obrazac stavke kao izlazni računi, pa se
 * stavka pri unosu prevodi u njegov oblik i natrag.
 */
const InvoiceIncomingEditItems: React.FC<InvoiceIncomingEditItemsProps> = (props) => {
  const { formValues, setFormValues } = props;

  const { t } = useTranslate();

  const [itemFormAdd, setItemFormAdd] = useState<InvoiceOutgoingItemEditFormValues>(
    transformInvoiceIncomingItemToForm(initialInvoiceIncomingItemEditForm)
  );
  const [itemId, setItemId] = useState<string>("");
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [showItemFormModal, setShowItemFormModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded(previous => !previous);
  }, []);

  const items = useMemo(() => formValues.items.filter(item => !item.deleted), [formValues.items]);

  const totals = useMemo(() => {
    const totalWithoutTax = items.reduce((sum, item) => sum + calculateBillingLineNet(item), 0);
    const total = items.reduce((sum, item) => sum + calculateBillingLineTotal(item), 0);

    return {
      totalWithoutTax: Number(totalWithoutTax.toFixed(2)),
      totalTax: Number((total - totalWithoutTax).toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  }, [items]);

  // PDV se razlaže po stopama, isto kao na izlaznim računima — po tome se zbroj provjerava
  // prema samom računu, na kojem stope stoje odvojeno.
  const taxTotalsByRate = useMemo(() => {
    const byRate: Record<number, number> = {};

    items.forEach(item => {
      const taxRate = item.taxRate ?? 0;
      const tax = calculateBillingLineTotal(item) - calculateBillingLineNet(item);

      byRate[taxRate] = (byRate[taxRate] ?? 0) + tax;
    });

    return Object.entries(byRate)
      .map(([taxRate, taxAmount]) => ({
        taxRate: Number(taxRate),
        taxAmount: Number(taxAmount.toFixed(2)),
      }))
      .sort((first, second) => first.taxRate - second.taxRate);
  }, [items]);

  const handleAddClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    setItemFormAdd(transformInvoiceIncomingItemToForm({
      ...initialInvoiceIncomingItemEditForm,
      id: crypto.randomUUID(),
    }));
    setItemId("");
    setIsEdit(false);
    setErrors({});
    setShowItemFormModal(true);
  }, []);

  const handleEditClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    const item = formValues.items.find(row => row.id === id);
    if (!item) return;

    setItemFormAdd(transformInvoiceIncomingItemToForm(item));
    setItemId(id);
    setIsEdit(true);
    setErrors({});
    setShowItemFormModal(true);
  }, [formValues.items]);

  const handleItemSubmit = useCallback((event: any) => {
    event.preventDefault();

    const isValid = validateYup(itemFormAdd, INVOICE_INCOMING_ITEM_EDIT_FORM_SCHEMA, setErrors);
    if (!isValid) return;

    const item = transformInvoiceIncomingItemFromForm(itemFormAdd);

    setFormValues(prev => ({
      ...prev,
      items: isEdit
        ? prev.items.map(row => row.id === item.id ? { ...row, ...item } : row)
        : [...prev.items, item],
    }));

    setShowItemFormModal(false);
    setIsEdit(false);
    setItemId("");
  }, [isEdit, itemFormAdd, setFormValues]);

  const handleItemFormModalClose = useCallback(() => {
    setShowItemFormModal(false);
    setIsEdit(false);
    setItemId("");
    setErrors({});
  }, []);

  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setItemId(id);
    setOpenDialog(true);
  }, []);

  const handleDeleteDialogConfirm = useCallback(() => {
    if (!itemId) return;

    // Stavka se označava obrisanom da poslužitelj zna što ukloniti pri spremanju.
    setFormValues(prev => ({
      ...prev,
      items: prev.items.map(item => item.id === itemId ? { ...item, deleted: true } : item),
    }));

    setOpenDialog(false);
    setItemId("");
  }, [itemId, setFormValues]);

  const handleDeleteDialogClose = useCallback(() => {
    setItemId("");
    setOpenDialog(false);
  }, []);

  const renderItems = () => (
    <>
      <div className="flex justify-end items-center gap-2 px-3 py-3">
        <Button
          variant="outlined"
          size="small"
          startIcon="/media/icons/plus.svg"
          onClick={handleAddClick}
        >
          Dodaj stavku
        </Button>

        {/* Jedna stavka se vidi i bez razvlačenja, pa se gumb nudi tek kad ih ima više. */}
        {items.length > 1 && (
          <Button
            variant="outlined"
            size="small"
            startIcon="/media/icons/scaling.svg"
            onClick={handleExpandToggle}
          />
        )}
      </div>

      {items.length > 0 ? (
        <>
          {/* Bez `overflow` na omotaču — njime bi se izbornik retka (Ažuriraj/Obriši) odrezao na
              donjem rubu tablice, jer `overflow` klipa i apsolutno pozicionirane potomke. */}
          <div className="px-3">
            <TableSearch
              columns={INVOICE_INCOMING_ITEMS_COLUMNS(handleEditClick, handleDeleteClick)}
              data={items}
              isLoading={false}
              selectableRows={false}
              disablePagination
            />
          </div>

          <div className="flex justify-end">
            <div className="w-[400px] px-3 pt-3 pb-3.5">
              <div className="py-2.5 space-y-1.5 bg-white rounded-t-sm border border-slate-200 overflow-hidden">
                <div className="flex justify-between text-sm text-slate-600 px-4">
                  <span>Ukupno bez PDV-a:</span>
                  <span className="font-medium tabular-nums">{formatCurrency.format(totals.totalWithoutTax)}</span>
                </div>

                {taxTotalsByRate.map(({ taxRate, taxAmount }) => (
                  <div key={taxRate} className="flex justify-between text-sm text-slate-600 px-4">
                    <span>PDV {taxRate}%:</span>
                    <span className="font-medium tabular-nums">{formatCurrency.format(taxAmount)}</span>
                  </div>
                ))}

                <div className="flex justify-between text-sm text-slate-900 font-medium pt-1.5 border-t border-slate-200 px-4">
                  <span>Ukupno s PDV-om:</span>
                  <span className="tabular-nums">{formatCurrency.format(totals.total)}</span>
                </div>
              </div>

              <div className="px-4 pt-2 pb-2.5 bg-sky-50 border-x border-b border-slate-200 flex justify-between items-center rounded-b-sm">
                <span className="text-sm font-semibold text-sky-800">Za plaćanje:</span>
                <span className="text-md font-semibold text-sky-700 tabular-nums">{formatCurrency.format(totals.total)}</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="px-3 pb-4 text-sm text-slate-500">Račun još nema stavki.</div>
      )}
    </>
  );

  return (
    <>
      {isExpanded ? (
        // Razvučeni popis ide u portal: `fixed` unutar modala bi ga odrezao stupac obrasca, koji
        // se skrola. Zauzima desnu polovicu, da dokument s lijeve strane ostane vidljiv.
        ReactDOM.createPortal(
          <div className="fixed top-0 bottom-0 left-0 lg:left-1/2 right-0 z-[60] bg-white border-l border-slate-200 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <span className="text-sm font-medium text-sky-600">Stavke računa</span>
              <Button variant="outlined" size="small" children="Zatvori" onClick={handleExpandToggle} />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2">{renderItems()}</div>
          </div>,
          document.body
        )
      ) : (
        renderItems()
      )}

      <Modal show={showItemFormModal} onHide={handleItemFormModalClose} position="centered" size="medium">
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={isEdit ? "Uređivanje stavke" : "Nova stavka"}
          onHide={handleItemFormModalClose}
        />
        <ModalBody>
          {showItemFormModal && (
            <>
              <InvoiceItemForm
                itemFormAdd={itemFormAdd}
                setItemFormAdd={setItemFormAdd}
                errors={errors}
                isEdit={isEdit}
                onSubmit={handleItemSubmit}
              />
              <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                <Button
                  variant="outlined"
                  children={t("admin.close")}
                  onClick={handleItemFormModalClose}
                />
                <Button
                  variant="primary"
                  children={isEdit ? t("admin.save") : "Dodaj stavku"}
                  onClick={handleItemSubmit}
                />
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <Dialog
        open={openDialog}
        title="Brisanje stavke"
        message="Jeste li sigurni da želite obrisati ovu stavku?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

export default memo(InvoiceIncomingEditItems);
